import { useId } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@antimatterhq/ui/lib/utils';
import { Text } from '@antimatterhq/ui/components/typography';

export type NavigationTab = {
  id: string;
  label: string;
};

export type NavigationTabsProps = {
  tabs: NavigationTab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  className?: string;
};

export default function NavigationTabs({
  tabs,
  activeTab,
  onTabChange,
  className,
}: NavigationTabsProps) {
  const uniqueId = useId();

  return (
    <div
      className={cn(
        'relative w-full max-w-3xl border-b border-neutral-100 dark:border-white/10',
        className,
      )}
    >
      <div className="flex space-x-4" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            className={cn(
              'relative px-4 py-2 text-sm font-medium transition-colors duration-200',
              activeTab === tab.id
                ? 'text-black dark:text-white'
                : 'text-grey-500 hover:text-blue-500 dark:text-grey-350 dark:hover:text-blue-400',
            )}
            onClick={() => onTabChange?.(tab.id)}
            data-testid={`tab-${tab.id}`}
            type="button"
          >
            <Text
              className={cn(
                activeTab === tab.id
                  ? 'text-black dark:text-white'
                  : 'text-grey-500 hover:text-blue-500 dark:text-grey-350 dark:hover:text-blue-400',
              )}
              size="sm"
            >
              {tab.label}
            </Text>
            {activeTab === tab.id && (
              <motion.div
                className="absolute bottom-0 left-0 right-0 z-10 h-0.5 bg-blue-500 dark:bg-blue-500"
                layoutId={`underline-${uniqueId}`}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
